import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Card } from '@/types'

type PanelSize = 'compact' | 'expanded'

let dismissTimer: NodeJS.Timeout | null = null

interface SidePanelStore {
  isOpen: boolean
  size: PanelSize
  activeTab: 'history' | 'toolbag'
  globalModalCard: Card | null
  hoverPreviewCard: Card | null
  toggle: () => void
  open: (tab?: 'history' | 'toolbag') => void
  close: () => void
  toggleSize: () => void
  setActiveTab: (tab: 'history' | 'toolbag') => void
  openCardModal: (card: Card) => void
  closeCardModal: () => void
  setHoverPreview: (card: Card | null) => void
  scheduleDismissPreview: () => void
  cancelDismissPreview: () => void
}

export const useSidePanelStore = create<SidePanelStore>()(
  persist(
    (set) => ({
      isOpen: false,
      size: 'compact',
      activeTab: 'history',
      globalModalCard: null,
      hoverPreviewCard: null,

      toggle: () => set((s) => ({ isOpen: !s.isOpen })),
      open: (tab) => set({ isOpen: true, ...(tab ? { activeTab: tab } : {}) }),
      close: () => set({ isOpen: false }),
      toggleSize: () => set((s) => ({ size: s.size === 'compact' ? 'expanded' : 'compact' })),
      setActiveTab: (tab) => set({ activeTab: tab }),
      openCardModal: (card) => set({ globalModalCard: card }),
      closeCardModal: () => set({ globalModalCard: null }),
      setHoverPreview: (card) => {
        if (dismissTimer) {
          clearTimeout(dismissTimer)
          dismissTimer = null
        }
        set({ hoverPreviewCard: card })
      },
      scheduleDismissPreview: () => {
        if (dismissTimer) clearTimeout(dismissTimer)
        dismissTimer = setTimeout(() => {
          set({ hoverPreviewCard: null })
          dismissTimer = null
        }, 200)
      },
      cancelDismissPreview: () => {
        if (dismissTimer) {
          clearTimeout(dismissTimer)
          dismissTimer = null
        }
      },
    }),
    {
      name: 'side-panel',
      partialize: (state) => ({ isOpen: state.isOpen, size: state.size, activeTab: state.activeTab }),
    }
  )
)
